/**
 * CR283: Snapshot Deal Decisions Panel
 * 
 * Displays deals in a snapshot with Accept/Hide toggles.
 * Used in both Live Preview Admin mode and S7A accordion.
 */
import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2,
  EyeOff,
  Clock,
  AlertCircle,
  Search,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { useSnapshotDealDecisions, type SnapshotDeal } from "@/hooks/useSnapshotDealDecisions";

interface SnapshotDealDecisionsPanelProps {
  snapshotId: string | null;
  compact?: boolean;
  maxHeight?: string;
}

type DecisionFilter = "all" | "accepted" | "hidden";
type ExpiryFilter = "all" | "active" | "expired";

export function SnapshotDealDecisionsPanel({
  snapshotId,
  compact = false,
  maxHeight = "400px",
}: SnapshotDealDecisionsPanelProps) {
  const {
    deals,
    loading,
    error,
    totalDeals,
    acceptedCount,
    hiddenCount,
    expiredCount,
    activeCount,
    refetch,
    toggleDecision,
  } = useSnapshotDealDecisions(snapshotId);

  const [searchQuery, setSearchQuery] = useState("");
  const [decisionFilter, setDecisionFilter] = useState<DecisionFilter>("all");
  const [expiryFilter, setExpiryFilter] = useState<ExpiryFilter>("all");
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Filter deals
  const filteredDeals = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    return deals.filter(deal => {
      // Search filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesSearch = 
          deal.headline_mechanic?.toLowerCase().includes(q) ||
          deal.merchant_name?.toLowerCase().includes(q) ||
          deal.deal_type?.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }

      // Decision filter
      if (decisionFilter === "accepted" && deal.decision !== "ACCEPTED") return false;
      if (decisionFilter === "hidden" && deal.decision !== "HIDDEN") return false;

      // Expiry filter
      const isExpired = deal.valid_until ? new Date(deal.valid_until) < now : false;
      if (expiryFilter === "active" && isExpired) return false;
      if (expiryFilter === "expired" && !isExpired) return false;

      return true;
    });
  }, [deals, searchQuery, decisionFilter, expiryFilter]);

  const handleToggle = async (deal: SnapshotDeal) => {
    setTogglingId(deal.id);
    const newDecision = deal.decision === "ACCEPTED" ? "HIDDEN" : "ACCEPTED";
    await toggleDecision(deal.id, newDecision);
    setTogglingId(null);
  };

  const isExpired = (deal: SnapshotDeal): boolean => {
    if (!deal.valid_until) return false;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return new Date(deal.valid_until) < now;
  };

  const formatDate = (date: string | null): string => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-CA");
  };

  if (!snapshotId) {
    return (
      <div className="text-sm text-muted-foreground p-4 text-center border rounded-lg bg-muted/30">
        No snapshot selected
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading deals...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border rounded-lg border-destructive/50 bg-destructive/10 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 inline mr-2" />
        Error: {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-0 space-y-3">
      {/* Header Stats - stays visible */}
      <div className="shrink-0 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="gap-1">
            <span className="text-muted-foreground">Total:</span> {totalDeals}
          </Badge>
          <Badge variant="default" className="gap-1 bg-primary/10 text-primary border-primary/30">
            <CheckCircle2 className="h-3 w-3" />
            {acceptedCount} Accepted
          </Badge>
          <Badge variant="secondary" className="gap-1">
            <EyeOff className="h-3 w-3" />
            {hiddenCount} Hidden
          </Badge>
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            {expiredCount} Expired
          </Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={refetch} className="gap-1 h-7">
          <RefreshCw className="h-3 w-3" />
          Refresh
        </Button>
      </div>

      {/* Filters - stays visible */}
      <div className="shrink-0 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search deals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-9"
          />
        </div>
        <Select value={decisionFilter} onValueChange={(v) => setDecisionFilter(v as DecisionFilter)}>
          <SelectTrigger className="w-[130px] h-9">
            <SelectValue placeholder="Decision" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="hidden">Hidden</SelectItem>
          </SelectContent>
        </Select>
        <Select value={expiryFilter} onValueChange={(v) => setExpiryFilter(v as ExpiryFilter)}>
          <SelectTrigger className="w-[130px] h-9">
            <SelectValue placeholder="Expiry" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Deals Table - scrollable with max-height cap */}
      <div className="flex-1 min-h-0 max-h-[50vh] overflow-y-auto border rounded-lg">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10 border-b">
            <TableRow>
              <TableHead className="w-[50px]">Show</TableHead>
              <TableHead>Deal</TableHead>
              <TableHead className="w-[100px]">Type</TableHead>
              <TableHead className="w-[100px]">Expires</TableHead>
              {!compact && <TableHead className="w-[90px]">Value</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDeals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={compact ? 4 : 5} className="text-center text-muted-foreground py-8">
                  No deals match filters
                </TableCell>
              </TableRow>
            ) : (
              filteredDeals.map((deal) => {
                const expired = isExpired(deal);
                const isToggling = togglingId === deal.id;

                return (
                  <TableRow 
                    key={deal.id} 
                    className={deal.decision === "HIDDEN" ? "opacity-50 bg-muted/30" : ""}
                  >
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center">
                              {isToggling ? (
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              ) : (
                                <Switch
                                  checked={deal.decision === "ACCEPTED"}
                                  onCheckedChange={() => handleToggle(deal)}
                                  disabled={isToggling}
                                />
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            {deal.decision === "ACCEPTED" ? "Click to hide from public" : "Click to show on public"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        <div className="font-medium text-sm line-clamp-1">
                          {deal.headline_mechanic || "No headline"}
                        </div>
                        <div className="text-xs text-muted-foreground line-clamp-1">
                          {deal.merchant_name || "Unknown merchant"}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {deal.deal_type || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {expired && (
                          <Clock className="h-3 w-3 text-warning" />
                        )}
                        <span className={expired ? "text-warning text-xs" : "text-xs"}>
                          {formatDate(deal.valid_until)}
                        </span>
                      </div>
                    </TableCell>
                    {!compact && (
                      <TableCell className="text-right font-mono text-sm">
                        {deal.computed_value_cad 
                          ? `$${deal.computed_value_cad.toFixed(2)}` 
                          : "—"}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Footer info - stays visible */}
      <div className="shrink-0 text-xs text-muted-foreground">
        Showing {filteredDeals.length} of {totalDeals} deals •{" "}
        Snapshot: <code className="bg-muted px-1 rounded">{snapshotId}</code>
      </div>
    </div>
  );
}
